import {ApiException} from "@/domain/core/exception/exceptions"

const state = {
    salesTotal: null,
    saasPaymentsTotal: null,
    saasPaymentsList: null,
    photoServicesByIndustry: {}, // Object with industry as key and photo services data as value
    // Common filters
    selectedDateRange: {},
    selectedIndustry: null,
    filterPhotoServiceName: null,
    isPromo: null
}

export const getters = {
    salesTotal: state => state.salesTotal,
    saasPaymentsTotal: state => state.saasPaymentsTotal,
    saasPaymentsList: state => state.saasPaymentsList,
    photoServicesByIndustry: state => industry => state.photoServicesByIndustry[industry] || null,
    allPhotoServicesByIndustry: state => state.photoServicesByIndustry,
    
    // Filters
    selectedDateRange: state => state.selectedDateRange,
    selectedIndustry: state => state.selectedIndustry,
    filterPhotoServiceName: state => state.filterPhotoServiceName,
    isPromo: state => state.isPromo,
    
    // Computed data
    totalAmountEur: state => state.salesTotal?.total_amount_eur || 0,
    totalHandlingFee: state => state.salesTotal?.total_handling_fee || 0,
    turnover: state => state.salesTotal?.turnover || 0,
    preSale: state => state.salesTotal?.presale || 0,
    industries: state => {
        if (!state.salesTotal?.by_industry) return []
        
        const industries = []
        for (const item of state.salesTotal.by_industry) {
            if (!industries.find(x => x.industry === item.industry)) {
                industries.push(item)
            }
        }
        return industries
    },
    
    // Common parameters getter
    commonParams: (state) => (industry = null) => {
        let params = {}

        if (state.selectedDateRange && state.selectedDateRange.start) {
            if (state.selectedDateRange.start) params['from'] = state.selectedDateRange.start
            if (state.selectedDateRange.end) params['to'] = state.selectedDateRange.end
        }

        if (!industry && state.selectedIndustry) params['industry'] = state.selectedIndustry
        if (industry) params['industry'] = industry
        params["merge_corporate_and_fun"] = true
        if (state.isPromo === true) {
            params.is_promo = true
        } else if (state.isPromo === false) {
            params.is_promo = false
        }
        if (state.filterPhotoServiceName) params.photo_service_name = state.filterPhotoServiceName
        return params
    }
}

export const mutations = {
    salesTotal(state, data) {
        state.salesTotal = data
    },
    
    saasPaymentsTotal(state, data) {
        state.saasPaymentsTotal = data
    },
    
    saasPaymentsList(state, data) {
        state.saasPaymentsList = data
    },
    
    setPhotoServicesByIndustry(state, {industry, data}) {
        state.photoServicesByIndustry = {
            ...state.photoServicesByIndustry,
            [industry]: data
        }
    },
    
    resetPhotoServicesByIndustry(state) {
        state.photoServicesByIndustry = {}
    },
    
    setPhotoServiceEvents(state, {industry, photoServiceId, events}) {
        const industryData = state.photoServicesByIndustry[industry]
        if (industryData && industryData.results) {
            const photoService = industryData.results.find(ps => ps.id === photoServiceId)
            if (photoService) {
                photoService.events = events
            }
        }
    },
    
    // Filter mutations
    selectedDateRange(state, dateRange) {
        state.selectedDateRange = dateRange
    },
    
    selectedIndustry(state, industry) {
        state.selectedIndustry = industry
    },
    
    filterPhotoServiceName(state, name) {
        state.filterPhotoServiceName = name
    },
    
    isPromo(state, isPromo) {
        state.isPromo = isPromo
    }
}

const actions = (salesReportService) => ({
    async fetchSalesTotal({commit, getters}, additionalParams = {}) {
        try {
            const params = {...getters.commonParams(), ...additionalParams}
            const response = await salesReportService.getSalesTotal(params)
            commit('salesTotal', response)
            return response
        } catch (error) {
            console.error('Error fetching sales total:', error)
            if (error instanceof ApiException) {
                commit('salesTotal', null)
            }
            throw error
        }
    },
    
    async fetchSaasPaymentsTotal({commit, getters}, additionalParams = {}) {
        try {
            const params = {...getters.commonParams(), ...additionalParams}
            const response = await salesReportService.getSaasPaymentsTotal(params)
            commit('saasPaymentsTotal', response)
            return response
        } catch (error) {
            console.error('Error fetching SaaS payments total:', error)
            if (error instanceof ApiException) {
                commit('saasPaymentsTotal', null)
            }
            throw error
        }
    },
    
    async fetchSaasPaymentsList({commit, getters}, ajaxParams = {}) {
        try {
            let params = {...getters.commonParams()}
            
            // Handle AjaxTable parameters
            if (ajaxParams.pagination) {
                params.page = ajaxParams.pagination.page
                params.page_size = ajaxParams.pagination.page_size
            }
            
            if (ajaxParams.sorting && ajaxParams.sorting.column) {
                const orderDirection = ajaxParams.sorting.asc ? '' : '-'
                params.ordering = `${orderDirection}${ajaxParams.sorting.column}`
            }
            
            if (ajaxParams.filters) {
                params = {...params, ...ajaxParams.filters}
            }
            
            const response = await salesReportService.getSaasPaymentsList(params)
            commit('saasPaymentsList', response)
            return response
        } catch (error) {
            console.error('Error fetching SaaS payments list:', error)
            if (error instanceof ApiException) {
                commit('saasPaymentsList', null)
            }
            throw error
        }
    },
    
    async fetchPhotoServiceTotals({commit}, {photoServiceId, params}) {
        try {
            const response = await SalesReportService.getPhotoServiceTotals(photoServiceId, params)
            return response
        } catch (error) {
            console.error('Error fetching photo service totals:', error)
            throw error
        }
    },
    
    async fetchPhotoServicesByIndustry({commit, getters, rootGetters}, industryKey) {
        try {
            // Check if data is already cached
            const cachedData = getters.photoServicesByIndustry(industryKey)
            if (cachedData) {
                return cachedData
            }
            
            // Get the industry data from salesTotal
            const salesTotal = getters.salesTotal
            if (!salesTotal || !salesTotal.by_industry) {
                throw new Error('Sales total data not available')
            }
            
            const industryData = salesTotal.by_industry.find(item => item.industry === industryKey)
            if (!industryData || !industryData.photo_services) {
                const emptyData = { results: [], pagination: { total: 0, page_size: 1000, page_number: 1, num_pages: 0 } }
                commit('setPhotoServicesByIndustry', {industry: industryKey, data: emptyData})
                return emptyData
            }
            
            
            // Build params for photo service totals
            let params = {}
            
            // Date range (highest priority)
            if (getters.selectedDateRange && getters.selectedDateRange.start) {
                params['from'] = getters.selectedDateRange.start
                if (getters.selectedDateRange.end) params['to'] = getters.selectedDateRange.end
            }
            
            // Industry (required)
            params['industry'] = industryKey
            
            // Photo service filter
            if (getters.filterPhotoServiceName) {
                params['photo_service_name'] = getters.filterPhotoServiceName
            }
            
            // Promo filter
            if (getters.isPromo === true) {
                params.is_promo = true
            } else if (getters.isPromo === false) {
                params.is_promo = false
            }
            
            params["merge_corporate_and_fun"] = true
            
            // Filter out null photo services and get totals for each
            let photoServices = industryData.photo_services.filter(x => x.id !== null)
            const pageSize = 1000
            const page = 1
            const numPages = Math.ceil(photoServices.length / pageSize)
            
            // Process first page
            const from = (page - 1) * pageSize
            const to = page * pageSize
            const photoServicesSliced = photoServices.slice(from, to)
            
            // Get totals for each photo service
            for (let photoService of photoServicesSliced) {
                try {
                    const photoServiceTotals = await salesReportService.getPhotoServiceTotals(photoService.id, params)
                    photoService.totals = photoServiceTotals
                } catch (error) {
                    console.error(`Error fetching totals for photo service ${photoService.id}:`, error)
                    photoService.totals = null
                }
            }
            
            // Sort by total amount (descending)
            const sortedResults = photoServicesSliced
                .filter(ps => ps.totals) // Filter out failed requests
                .sort((a, b) => (b.totals.total_amount_eur || 0) - (a.totals.total_amount_eur || 0))
            
            const data = {
                results: sortedResults,
                pagination: {
                    total: photoServices.length,
                    page_size: pageSize,
                    page_number: page,
                    num_pages: numPages
                }
            }
            
            
            // Cache the data
            commit('setPhotoServicesByIndustry', {industry: industryKey, data})
            
            return data
        } catch (error) {
            console.error('Error fetching photo services by industry:', error)
            throw error
        }
    },
    

    async fetchPhotoServiceEventsTotals({commit, getters}, {photoServiceId, industry, params = {}}) {
        try {
            // Build params with proper priority
            let finalParams = {}
            
            // Date range (highest priority)
            if (getters.selectedDateRange && getters.selectedDateRange.start) {
                finalParams['from'] = getters.selectedDateRange.start
                if (getters.selectedDateRange.end) finalParams['to'] = getters.selectedDateRange.end
            }
            
            // Industry (required)
            finalParams['industry'] = industry
            
            // Photo service filter
            if (getters.filterPhotoServiceName) {
                finalParams['photo_service_name'] = getters.filterPhotoServiceName
            }
            
            // Promo filter
            if (getters.isPromo === true) {
                finalParams.is_promo = true
            } else if (getters.isPromo === false) {
                finalParams.is_promo = false
            }
            
            finalParams["merge_corporate_and_fun"] = true
            
            // Merge with additional params (like ordering, pagination)
            finalParams = { ...finalParams, ...params }
            
            const response = await salesReportService.getPhotoServiceEventsTotals(photoServiceId, finalParams)
            
            // Store the events in the photo service
            commit('setPhotoServiceEvents', {
                industry,
                photoServiceId,
                events: response
            })
            
            return response
        } catch (error) {
            console.error('Error fetching photo service events totals:', error)
            throw error
        }
    },
    
    setPhotoServicesByIndustry({commit}, {industry, data}) {
        commit('setPhotoServicesByIndustry', {industry, data})
    },
    
    resetPhotoServicesByIndustry({commit}) {
        commit('resetPhotoServicesByIndustry')
    },
    
    // Filter actions
    setSelectedDateRange({commit}, dateRange) {
        commit('selectedDateRange', dateRange)
    },
    
    setSelectedIndustry({commit}, industry) {
        commit('selectedIndustry', industry)
    },
    
    setFilterPhotoServiceName({commit}, name) {
        commit('filterPhotoServiceName', name)
    },
    
    setIsPromo({commit}, isPromo) {
        commit('isPromo', isPromo)
    }
})

export default (salesReportService) => ({
    namespaced: true,
    state,
    getters,
    mutations,
    actions: actions(salesReportService)
})